import { rem } from "@certn/cascadia";
import {
  BasicButton,
  Divider,
  GridItem,
  Loader,
  Text,
} from "@certn/cascadia-react";
import { S3FileInfo } from "@certn/cascadia-react/deprecated/upload/types";
import { usePeopleAPI } from "@certn/legacy-api";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { useField } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { CommencementInfoModal } from "./CommencementInfoModal";
import { CategoryType, DocumentField } from "./DocumentField";
import { PrimaryInfoModal } from "./PrimaryInfoModal";
import { SecondaryInfoModal } from "./SecondaryInfoModal";
import { SupportingInfoModal } from "./SupportingInfoModal";
import { ApplicantDocumentUpload } from "../../../../components/ApplicantDocumentUpload";

export function IdentificationDocumentsInputField({
  field,
}: {
  field: components["schemas"]["IdentificationDocumentsCheckField"];
}) {
  const { applicantId } = useParams();
  const { t } = useTranslation();
  const [, , helpers] = useField(field.name);
  const [files, setFiles] = useState<
    Record<
      CategoryType,
      components["schemas"]["IdentificationDocumentsCheckField"]["value"]
    >
  >({
    commencement: field.value.filter(
      ({ document_subtype }) => document_subtype === "commencement"
    ),
    primary: field.value.filter(
      ({ document_subtype }) => document_subtype === "primary"
    ),
    secondary: field.value.filter(
      ({ document_subtype }) => document_subtype === "secondary"
    ),
    supporting: field.value.filter(
      ({ document_subtype }) => document_subtype === "supporting"
    ),
  });

  const { data: documentTypes, isLoading: documentTypesLoading } = usePeopleAPI<
    "/internal/people/document-types/",
    Record<CategoryType, { key: string; value: string }[]>
  >("/internal/people/document-types/", {
    check: "acic",
  });

  useEffect(() => {
    // convert to a list of files
    helpers.setValue(Object.values(files).reduce((i, j) => [...i, ...j], []));
  }, [files]);

  const updateFiles = (
    key: CategoryType,
    files: components["schemas"]["IdentificationDocumentsCheckField"]["value"]
  ) => {
    setFiles((prev) => ({
      ...prev,
      [key]: files,
    }));
  };

  const updateSecondaryFiles = (
    first: boolean,
    files: components["schemas"]["IdentificationDocumentsCheckField"]["value"]
  ) => {
    // since we have 2 fields to update "secondary" type files,
    // we need 2 ways to save files, hence this is what "first" parameter is for
    // If "first" is true, we replace files[0] in state, else we keep files[0] and overwrite the rest
    setFiles((prev) => ({
      ...prev,
      secondary: first
        ? [...files, ...prev.secondary.slice(1)]
        : [...prev.secondary.slice(0, 1), ...files],
    }));
  };

  const supportingS3Files: S3FileInfo[] = useMemo(
    () =>
      files.supporting.map((file) => ({
        document: {
          document_id: file.id,
        } as S3FileInfo["document"],
        type: file.file_type,
        name: file.file_name,
        url: file.get_url,
        size: 0,
      })),
    [files.supporting]
  );

  if (documentTypesLoading) {
    // need to wait for types to load before displaying files
    // removing this will break what "subtitle" for each file displays
    return (
      <GridItem>
        <Loader />
      </GridItem>
    );
  }

  return (
    <>
      <GridItem>
        <Divider />
      </GridItem>

      <GridItem className={css({ gap: rem("space-2") })}>
        <Text style="form/section-heading">
          <Trans>Required documents</Trans>
        </Text>
        <Text style="form/body-secondary" color="text-weak">
          <Trans>File types accepted: PDF, JPG or PNG (max 10 MB)</Trans>
        </Text>
      </GridItem>

      <DocumentField
        type="commencement"
        label={t("Commencement document")}
        info={<CommencementInfoModal />}
        files={files.commencement}
        onChange={(files) => updateFiles("commencement", files)}
      />

      <DocumentField
        type="primary"
        label={t("Primary document")}
        info={<PrimaryInfoModal />}
        files={files.primary}
        onChange={(files) => updateFiles("primary", files)}
      />

      <DocumentField
        type="secondary"
        label={t("Secondary document (1 of 2)")}
        info={<SecondaryInfoModal />}
        files={files.secondary.slice(0, 1)}
        onChange={(files) => updateSecondaryFiles(true, files)}
      />

      <DocumentField
        type="secondary"
        label={t("Secondary document (2 of 2)")}
        info={<SecondaryInfoModal />}
        files={files.secondary.slice(1)}
        onChange={(files) => updateSecondaryFiles(false, files)}
      />

      <GridItem>
        <Divider />
      </GridItem>

      <GridItem className={css({ gap: rem("space-2") })}>
        <Text style="form/section-heading">
          <Trans>Supporting documents (optional)</Trans>
        </Text>
        <Text style="form/body-secondary" color="text-weak">
          <Trans>
            Supporting documents may be used to validate the required
            identification documents.
          </Trans>{" "}
          <SupportingInfoModal />
        </Text>
      </GridItem>

      <GridItem>
        <ApplicantDocumentUpload
          multiple
          documentType={documentTypes?.supporting?.[0]?.key ?? ""}
          documentSubtype="supporting"
          applicantId={applicantId ?? ""}
          renderTrigger={(openFileBrowser: () => void) => (
            <BasicButton
              leftIcon="add"
              size="medium"
              testID="identification-documents-supporting-document-add-button"
              onPress={openFileBrowser}
            >
              <Trans>Add document</Trans>
            </BasicButton>
          )}
          files={supportingS3Files}
          setFiles={(files) => {
            updateFiles(
              "supporting",
              files.map(({ document, name, type }) => ({
                id: document.document_id,
                document_type: documentTypes?.supporting?.[0]?.key ?? "",
                document_subtype: type,
                file_name: name,
                file_type: type,
                get_url: "",
              }))
            );
          }}
        />
      </GridItem>
    </>
  );
}
